import { useQuery } from '@tanstack/react-query';

import { COHORTS_PATH } from '@verantos-dev/core-constants';

import { authFetch } from './fetchers';
import { parseOrFallback } from './parseOrFallback';
import { cohortQueryKeys } from './queryKeys';
import { cohortUsageSchema, objectResponse, type CohortUsageFromApi } from './schemas';

export interface AnalyticalDatasetUsageRef {
  analyticalDatasetProfileId: string;
  name: string;
  /** The AD's own latest status (DRAFT/PROCESSING/PROCESSED/ERROR), when the
   * backend included it. Used to block cohort *reprocessing* while a
   * dependent AD is actively PROCESSING. */
  latestStatus?: string;
}

export interface CohortUsageView {
  cohortProfileId: string;
  isUsed: boolean;
  analyticalDatasets: AnalyticalDatasetUsageRef[];
}

interface UseCohortUsageQueryParams {
  projectId: string;
  cohortId: string;
  /** Set false to skip the read; defaults to enabled. */
  enabled?: boolean;
}

/**
 * TanStack read for `/cohorts/<projectId>/cohort-profile/<cohortId>/usage` —
 * the analytical datasets whose latest version references this cohort.
 *
 * Drives both the cohort-builder edit-lock and the delete-modal in-use guard.
 * Derive `isUsed` from the list so a tolerant/partial payload can't desync the
 * boolean from the names we render.
 */
export const useCohortUsageQuery = ({
  projectId,
  cohortId,
  enabled,
}: UseCohortUsageQueryParams) => {
  return useQuery<CohortUsageView>({
    queryKey: cohortQueryKeys.usage(cohortId),
    queryFn: async () => {
      const raw = await authFetch<unknown>(
        `/${COHORTS_PATH}/${projectId}/cohort-profile/${cohortId}/usage`
      );
      const envelope = parseOrFallback(
        objectResponse(cohortUsageSchema),
        raw,
        'useCohortUsageQuery'
      );
      const parsed = envelope.data as CohortUsageFromApi;
      const analyticalDatasets = parsed.analyticalDatasets.map((ad) => ({
        analyticalDatasetProfileId: ad.analyticalDatasetProfileId,
        name: ad.name,
        latestStatus: ad.latestStatus,
      }));
      return {
        cohortProfileId: parsed.cohortProfileId,
        isUsed: analyticalDatasets.length > 0,
        analyticalDatasets,
      };
    },
    enabled: enabled !== false && !!projectId && !!cohortId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
};
